import { useState, useRef, useEffect } from 'react';
import {
  FaSatelliteDish,
  FaRocket,
  FaWhatsapp,
  FaArrowRight,
  FaClock,
  FaShieldHalved,
} from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

/* ============================================
   HOOK: Network Canvas (Sama persis dengan Login)
   ============================================ */
function useNetworkCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(60, Math.floor((canvas.width * canvas.height) / 20000));
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.8 + 0.6,
      o: Math.random() * 0.35 + 0.1,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const maxD = 130;
    const mouseD = 180;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        const dm = Math.hypot(mouse.x - n.x, mouse.y - n.y);
        let op = n.o;
        let rad = n.r;
        if (dm < mouseD) {
          op = Math.min(1, op + (1 - dm / mouseD) * 0.5);
          rad += (1 - dm / mouseD) * 2;
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,180,216,${op})`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < maxD) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,180,216,${(1 - d / maxD) * 0.1})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        const dm = Math.hypot(mouse.x - nodes[i].x, mouse.y - nodes[i].y);
        if (dm < mouseD) {
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(72,202,228,${(1 - dm / mouseD) * 0.2})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [canvasRef]);
}

/* ============================================
   KOMPONEN: Coming Soon
   ============================================ */
export const ComingSoon = () => {
  const canvasRef = useRef(null);
  useNetworkCanvas(canvasRef);
  const [mounted, setMounted] = useState(false);

  // Konfigurasi WhatsApp (Ganti nomor HP di sini)
  const whatsappNumber = "6283142430713"; 
  const message = encodeURIComponent("Halo Admin Radius Trend, saya ingin meminta akses fitur baru / memberikan masukan.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${message}`;

  // Trigger mount animation
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <Helmet>
        <title>Coming Soon | Radius Trend</title>
        <meta
          name="description"
          content="Kami sedang mempersiapkan fitur terbaik untuk manajemen jaringan Anda. Hubungi kami untuk info lebih lanjut."
        />
      </Helmet>

      <main className="relative min-h-screen flex items-center justify-center bg-navy-900 p-4 sm:p-6 overflow-hidden">
        {/* ===== Network Canvas Background ===== */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full z-0"
          aria-hidden="true"
        />

        {/* ===== Gradient Orbs (Posisi disesuaikan untuk variasi) ===== */}
        <div
          className="absolute w-[500px] h-[500px] bg-ocean-900/20 top-[10%] -left-[10%] rounded-full"
          style={{ filter: 'blur(120px)', animation: 'float 10s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[400px] h-[400px] bg-ocean-800/10 bottom-[10%] -right-[10%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'floatSlow 14s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[300px] h-[300px] bg-purple-900/10 top-[40%] left-[40%] rounded-full"
          style={{ filter: 'blur(80px)', animation: 'float 12s ease-in-out 2s infinite' }}
          aria-hidden="true"
        />

        {/* ===== Card Utama ===== */}
        <article
          className={`
            relative z-10 max-w-4xl w-full
            rounded-3xl overflow-hidden min-h-[500px]
            transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1)
            flex flex-col md:flex-row
            ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.97]'}
          `}
          style={{
            background: 'rgba(11, 20, 38, 0.65)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(0, 180, 216, 0.1)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 180, 216, 0.06)',
          }}
        >
          {/* ===== Left Panel — Visual ===== */}
          <section
            className="w-full md:w-1/2 p-10 md:p-14 flex flex-col items-center justify-center text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, rgba(0, 119, 182, 0.2) 0%, rgba(6, 13, 31, 0.4) 100%)',
              borderBottom: '1px solid rgba(0, 180, 216, 0.1)',
              md: { borderBottom: 'none', borderRight: '1px solid rgba(0, 180, 216, 0.1)' }
            }}
          >
            {/* Decorative Elements */}
            <div
              className="absolute top-0 left-0 w-full h-full opacity-[0.05]"
              style={{
                background: 'radial-gradient(circle at center, #48CAE4 0%, transparent 70%)',
              }}
            />

            <div className="relative z-10 flex flex-col items-center">
              {/* Animated Icon */}
              <div className="relative mb-8">
                <div
                  className="absolute inset-0 bg-ocean-500/20 rounded-full animate-ping"
                  style={{ animationDuration: '3s' }}
                ></div>
                <div
                  className="relative w-24 h-24 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(0, 119, 182, 0.1))',
                    border: '1px solid rgba(0, 180, 216, 0.3)',
                    boxShadow: '0 0 30px rgba(0, 180, 216, 0.2)',
                    animation: 'float 6s ease-in-out infinite'
                  }}
                >
                  <FaRocket className="text-4xl text-ocean-500 transform -rotate-45" />
                </div>
              </div>

              <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight">
                Coming <span className="text-ocean-500">Soon</span>
              </h1>
              <p className="text-slate-custom text-sm md:text-base">
                Sesuatu yang hebat sedang dibangun.
              </p>
            </div>
          </section>

          {/* ===== Right Panel — Content & Action ===== */}
          <section className="w-full md:w-1/2 p-10 md:p-14 flex flex-col justify-center relative">
            {/* Brand Header */}
            <div className="flex items-center gap-3 mb-8">
               <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 119, 182, 0.3), rgba(0, 180, 216, 0.15))',
                  border: '1px solid rgba(0, 180, 216, 0.2)',
                }}
              >
                <FaSatelliteDish className="text-ocean-500 text-lg" />
              </div>
              <span className="font-display font-bold text-xl text-white">
                Radius<span className="text-ocean-500">Trend</span>
              </span>
            </div>

            <h2 className="font-display text-2xl font-bold text-white mb-4">
                Kami sedang merapikan semuanya.
            </h2>
            
            <p className="text-slate-custom text-sm leading-relaxed mb-8">
              Halaman ini sedang dalam tahap pengembangan untuk memberikan pengalaman terbaik bagi Anda. Kami akan segera kembali dengan pembaruan fitur yang lebih canggih.
            </p>

            {/* Feature Request Highlight */}
            <div className="bg-navy-700/30 rounded-xl p-5 border border-ocean-600/20 mb-8">
                <div className="flex items-start gap-4">
                    <div className="mt-1 p-2 bg-ocean-500/10 rounded-lg">
                        <FaClock className="text-ocean-500" />
                    </div>
                    <div>
                        <h3 className="text-white font-semibold text-sm mb-1">Ingin akses lebih cepat?</h3>
                        <p className="text-slate-custom text-xs">
                            Hubungi tim kami melalui WhatsApp untuk request akses fitur atau demo sistem.
                        </p>
                    </div>
                </div>
            </div>

            {/* Action Button */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center text-base py-4 group"
            >
              <FaWhatsapp className="text-lg" />
              <span>Hubungi Admin (WhatsApp)</span>
              <FaArrowRight className="text-base opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>

            {/* Footer inside card */}
            <div className="mt-8 flex justify-between items-center text-xs text-slate-custom/40 border-t border-white/5 pt-6">
              <Link to="/" className="hover:text-ocean-500 transition-colors">
                  &larr; Kembali ke Beranda
              </Link>
              <div className="flex items-center gap-1">
                <FaShieldHalved className="text-[10px]" />
                <span>Secured Connection</span>
              </div>
            </div>
          </section>
        </article>

        {/* ===== Bottom Copyright ===== */}
        <p
          className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-custom/30 z-10"
          style={{ animation: 'fadeIn 1s ease-out 1s both' }}
        >
          &copy; {new Date().getFullYear()} Radius Trend. All rights reserved.
        </p>
      </main>

      {/* ===== Inline Styles (Copy dari Login untuk konsistensi) ===== */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-body { font-family: 'Plus Jakarta Sans', sans-serif; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; background-color: #060D1F; }

        /* Warna Utilitas */
        .bg-navy-900 { background-color: #060D1F; }
        .bg-navy-700\/30 { background-color: rgba(15, 26, 46, 0.3); }
        .text-slate-custom { color: #8899AA; }
        .text-ocean-500 { color: #48CAE4; }
        .text-ocean-400 { color: #90E0EF; }
        .border-ocean-600\/20 { border-color: rgba(0, 180, 216, 0.2); }

        /* Button Styles */
        .btn-primary {
          background: linear-gradient(135deg, #0077B6, #00B4D8);
          color: #fff;
          padding: 14px 24px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-family: inherit;
          box-shadow: 0 4px 15px rgba(0, 119, 182, 0.3);
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.6s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 180, 216, 0.5);
        }
      `}</style>
    </>
  );
};